import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { TenantContextValue, TenantInfo, TenantSettings } from './types';
import { resolveTenant } from './tenantResolver';

const TenantContext = createContext<TenantContextValue | null>(null);

// Header name for tenant identification (ABP standard)
export const TENANT_HEADER = '__tenant';

interface TenantProviderProps {
  children: ReactNode;
}

/**
 * Fetch tenant info from ABP API
 */
const fetchTenantByName = async (
  tenantName: string
): Promise<TenantInfo | null> => {
  try {
    const response = await apiClient.get<{
      success: boolean;
      tenantId?: string;
      name?: string;
      isActive?: boolean;
    }>(`/api/abp/multi-tenancy/tenants/by-name/${encodeURIComponent(tenantName)}`, {
      headers: { [TENANT_HEADER]: tenantName },
    });

    if (response.data.success && response.data.tenantId) {
      return {
        id: response.data.tenantId,
        name: response.data.name || tenantName,
        isActive: response.data.isActive ?? true,
      };
    }
    return null;
  } catch {
    // Tenant not found or API error - return null
    return null;
  }
};

/**
 * Default blob storage URL (same as Angular app)
 */
const DEFAULT_BLOB_STORAGE_URL = 'https://motrv2assets.blob.core.windows.net';

/**
 * Fetch blob storage URL from settings
 * This is used to construct tenant logo URLs dynamically (like Angular)
 */
const fetchBlobStorageUrl = async (): Promise<string> => {
  try {
    const response = await apiClient.get<Array<{ name: string; value: string }>>(
      '/api/app/settings/settings',
      { params: { names: 'motr.Settings.BlobStorageUrl' } }
    );
    const setting = response.data?.find(s => s.name === 'motr.Settings.BlobStorageUrl');
    return setting?.value || DEFAULT_BLOB_STORAGE_URL;
  } catch {
    return DEFAULT_BLOB_STORAGE_URL;
  }
};

/**
 * Build tenant logo URL from blob storage
 * Pattern matches Angular: {blobStorageUrl}/www/{tenantName}_Logo.png
 * @param blobStorageUrl - Base Azure blob storage URL
 * @param tenantName - Tenant name (or 'www' for host)
 */
const buildLogoUrl = (blobStorageUrl: string, tenantName: string): string => {
  const safeTenantName = tenantName || 'www';
  return `${blobStorageUrl}/www/${safeTenantName}_Logo.png`;
};

/**
 * Fetch tenant-specific settings
 */
const fetchTenantSettings = async (
  tenantName: string | null
): Promise<TenantSettings> => {
  try {
    // First, get the blob storage URL (same approach as Angular)
    const blobStorageUrl = await fetchBlobStorageUrl();

    // Build dynamic logo URL using tenant name pattern (like Angular)
    // When no tenant, use 'www' as fallback (like Angular)
    const effectiveTenantName = tenantName || 'www';
    const dynamicLogoUrl = buildLogoUrl(blobStorageUrl, effectiveTenantName);

    // Also fetch any explicit tenant settings
    const response = await apiClient.get<{
      items: Array<{ name: string; value: string }>;
    }>('/api/app/settings/motr-settings', {
      headers: tenantName ? { [TENANT_HEADER]: tenantName } : {},
    });

    const settings: TenantSettings = {
      // Default to dynamic logo URL (Azure blob storage pattern)
      logoUrl: dynamicLogoUrl,
    };

    // Override with explicit settings if they exist
    for (const item of response.data.items || []) {
      switch (item.name) {
        case 'motr.Settings.Logo':
        case 'motr.Settings.LogoUrl':
          // Only override if explicitly set
          if (item.value) {
            settings.logoUrl = item.value;
          }
          break;
        case 'motr.Settings.Favicon':
          settings.faviconUrl = item.value;
          break;
        case 'motr.Settings.ThemeOverride':
          settings.themeOverride = item.value;
          break;
        case 'motr.Settings.CSSOverride':
          settings.cssOverride = item.value;
          break;
        case 'motr.Settings.CompanyName':
          settings.companyName = item.value;
          break;
        case 'motr.Settings.SupportEmail':
          settings.supportEmail = item.value;
          break;
      }
    }
    // Fetch uploaded light/dark logo URLs from branding API
    try {
      const logoResponse = await apiClient.get<{
        lightLogoUrl: string | null;
        darkLogoUrl: string | null;
      }>('/api/app/branding/logo/urls', {
        headers: tenantName ? { [TENANT_HEADER]: tenantName } : {},
      });
      if (logoResponse.data.lightLogoUrl) {
        settings.logoLightUrl = logoResponse.data.lightLogoUrl;
      }
      if (logoResponse.data.darkLogoUrl) {
        settings.logoDarkUrl = logoResponse.data.darkLogoUrl;
      }
    } catch {
      // Logo URLs are optional — continue without them
    }

    return settings;
  } catch {
    // Even on error, return dynamic logo URL as fallback
    const effectiveTenantName = tenantName || 'www';
    return {
      logoUrl: `${DEFAULT_BLOB_STORAGE_URL}/www/${effectiveTenantName}_Logo.png`,
    };
  }
};

/**
 * Tenant Provider - Resolves and provides tenant context
 *
 * Tenant is resolved from:
 * 1. Subdomain (e.g., acme.mot-r.app)
 * 2. Query parameter (?__tenant=acme)
 * 3. localStorage (for persistence)
 */
export const TenantProvider = ({ children }: TenantProviderProps) => {
  const [tenantName] = useState(() => resolveTenant());

  // Fetch tenant info
  const {
    data: tenant,
    isLoading: isTenantLoading,
  } = useQuery({
    queryKey: ['tenant', tenantName],
    queryFn: () => (tenantName ? fetchTenantByName(tenantName) : null),
    enabled: !!tenantName,
    staleTime: 60 * 60 * 1000, // Cache for 1 hour
    gcTime: 24 * 60 * 60 * 1000, // Keep in cache for 24 hours
  });

  // Fetch tenant settings (always runs - for logo even without tenant)
  const {
    data: settings = {} as TenantSettings,
    isLoading: isSettingsLoading,
  } = useQuery<TenantSettings>({
    queryKey: ['tenant-settings', tenantName || 'www'],
    queryFn: () => fetchTenantSettings(tenantName),
    // Always enabled - we need logo URL even for host
    enabled: true,
    staleTime: 15 * 60 * 1000, // Cache for 15 minutes
  });

  // Apply tenant CSS override
  useEffect(() => {
    if (settings?.cssOverride) {
      const styleId = 'tenant-css-override';
      let styleElement = document.getElementById(styleId) as HTMLStyleElement;

      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }

      styleElement.textContent = settings.cssOverride;
    }

    return () => {
      const styleElement = document.getElementById('tenant-css-override');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [settings?.cssOverride]);

  // Apply favicon if tenant has custom one
  useEffect(() => {
    if (settings?.faviconUrl) {
      const link = document.querySelector("link[rel='icon']") as HTMLLinkElement;
      if (link) {
        link.href = settings.faviconUrl;
      }
    }
  }, [settings?.faviconUrl]);

  // Update document title with company name
  useEffect(() => {
    if (settings?.companyName) {
      const baseTitle = 'mot-r';
      document.title = `${settings.companyName} - ${baseTitle}`;
    }
  }, [settings?.companyName]);

  const value = useMemo<TenantContextValue>(
    () => ({
      tenant: tenant || null,
      settings: settings || {},
      isLoading: isTenantLoading || isSettingsLoading,
      isHost: !tenantName,
      tenantName,
    }),
    [tenant, settings, isTenantLoading, isSettingsLoading, tenantName]
  );

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};

/**
 * Hook to access tenant context
 */
export const useTenantContext = (): TenantContextValue => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenantContext must be used within a TenantProvider');
  }
  return context;
};

export default TenantProvider;
